import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Box, CircularProgress, Typography } from "@mui/material"
import { fetchConnectStatus } from "../../../../redux/specialist-slices/wallet.slice"
import { showToast } from "../../../../redux/guest-slices/other.slice"

/**
 * ConnectSuccess
 *
 * Stripe redirects specialists here after completing the onboarding form.
 * We poll the server for the latest status and then navigate to the wallet page.
 *
 * Route: /specialist/profile/connect/success
 */
function ConnectSuccess() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    let cancelled = false
    let attempts = 0
    const maxAttempts = 6     // up to ~13.5s total
    const RETRY_DELAY = 2000  // ms between retries
    let timer = null

    const poll = async () => {
      if (cancelled) return
      attempts++

      const action = await dispatch(fetchConnectStatus())

      if (cancelled) return

      if (fetchConnectStatus.fulfilled.match(action)) {
        const status = action.payload
        if (status?.onboardingComplete) {
          dispatch(showToast({ type: "success", message: "🎉 Stripe account fully verified! Payouts are now enabled." }))
          navigate("/specialist/profile/wallet?from=onboarding", { replace: true })
          return
        }
        if (status?.detailsSubmitted) {
          dispatch(showToast({ type: "info", message: "Details submitted — Stripe is reviewing your account." }))
          navigate("/specialist/profile/wallet?from=onboarding", { replace: true })
          return
        }
      }

      // Not yet updated — retry if under the limit
      if (attempts < maxAttempts) {
        timer = setTimeout(poll, RETRY_DELAY)
      } else {
        // Give up polling; navigate anyway so the spinner doesn't hang forever
        navigate("/specialist/profile/wallet?from=onboarding", { replace: true })
      }
    }

    // Short initial delay to allow Stripe webhooks to propagate
    timer = setTimeout(poll, 1500)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [dispatch, navigate])

  return (
    <Box
      sx={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          background: "linear-gradient(135deg,#d1fae5,#a7f3d0)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "36px",
        }}
      >
        ✓
      </Box>
      <Typography variant="h6" fontWeight={700} color="other.color1">
        Onboarding Complete!
      </Typography>
      <Typography color="text.secondary" fontSize="14px" textAlign="center" maxWidth={340}>
        Verifying your account status with Stripe…
      </Typography>
      <CircularProgress size={28} thickness={4} />
    </Box>
  )
}

export default ConnectSuccess
